import { useState, useEffect } from 'react';

import { MenuItem, Select, TextField } from '@mui/material';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import NodeControlForm from '../NodeControlForm';

import type { NodeControlsProps } from '../../dataProvider/types';

export interface SpeechData extends Record<string, unknown> {
  auth_key?: string;
  encoding?: 'mp3' | 'wav';
  language?: 'it' | 'en';
  text?: string;
}

interface SpeechControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: SpeechData;
}

export default function SpeechControls({ updateNodeData, data }: SpeechControlsProps): React.ReactElement {
  const [authKey, setAuthKey] = useState<string>(data.auth_key ?? '');
  const [encoding, setEncoding] = useState<'mp3' | 'wav'>(data.encoding ?? 'wav');
  const [language, setLanguage] = useState<'it' | 'en'>(data.language ?? 'it');
  const [text, setText] = useState<string>(data.text ?? '');

  const translate = useTranslate();

  const onSpeechDataUpdate = (event: React.MouseEvent | React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('SpeechControls: onSpeechDataUpdate', { authKey, encoding, language, text });
    if (authKey === undefined || authKey === '') {
      flowchartLogger.debug('SpeechControls: authKey is undefined or empty. Update cancelled.');
      return;
    }
    if (text === undefined || text === '') {
      flowchartLogger.debug('SpeechControls: text is undefined or empty. Update cancelled.');
      return;
    }
    updateNodeData({ auth_key: authKey, encoding, language, text });
  };

  useEffect(() => {
    flowchartLogger.debug('SpeechControls: props changed', { authKey, encoding, language, text });
    updateNodeData({ auth_key: authKey, encoding, language, text });
  }, [authKey, encoding, language, text, updateNodeData]);

  return (
    <NodeControlForm
      onSubmit={onSpeechDataUpdate}
      isDisabled={authKey === '' || text === ''}
      data={data}
      updateNodeData={updateNodeData}
    >
      <TextField
        value={authKey}
        onChange={(e) => {
          setAuthKey(e.target.value);
        }}
        label={translate('callflows.nodes.speech.auth_key')}
      />
      <Select
        required
        variant='outlined'
        value={encoding}
        label={translate('callflows.nodes.speech.encoding')}
        onChange={(event) => {
          event.target.value === 'mp3' ? setEncoding('mp3') : setEncoding('wav');
        }}
        sx={{ mb: 1 }}
      >
        <MenuItem value='wav'>{translate('callflows.nodes.speech.encodingWav')}</MenuItem>
        <MenuItem value='mp3'>{translate('callflows.nodes.speech.encodingMp3')}</MenuItem>
      </Select>
      <Select
        required
        variant='outlined'
        value={language}
        label={translate('callflows.nodes.speech.language')}
        onChange={(event) => {
          event.target.value === 'it' ? setLanguage('it') : setLanguage('en');
        }}
        sx={{ mb: 1 }}
      >
        <MenuItem value='it'>{translate('callflows.nodes.speech.languageIt')}</MenuItem>
        <MenuItem value='en'>{translate('callflows.nodes.speech.languageEn')}</MenuItem>
      </Select>
      <TextField
        value={text}
        onChange={(e) => {
          setText(e.target.value);
        }}
        label={translate('callflows.nodes.speech.text')}
        multiline
        rows={4}
      />
    </NodeControlForm>
  );
}
